import { tw } from "@/utils";

import FollowUpIcon from "@/assets/followUpIcon.svg?react";
import { useParams } from "react-router";
import { HospitalResource, useGetHospitalsResource } from "../api";
import { useEffect, useState } from "react";
import { operationCodeToName } from "@/data/operations";
import { equipmentCodeToName } from "@/data/equipments";
import { bedCodeToName } from "@/data/beds";
import { BedChangeIcon } from "./BedChangeIcon";
import { useFollowUpHospitalStore } from "@/store/FollowUpHospital";
import { hospitalNameToShort } from "@/data/hospital";

interface HospitalResourceGridProps {
  // props 정의
}

export const HospitalResourceGrid = (props: HospitalResourceGridProps) => {
  const {} = props;
  const { requestId } = useParams();
  const { followUpHospital } = useFollowUpHospitalStore();

  if (!requestId) throw new Error();

  const { data, isSuccess } = useGetHospitalsResource(requestId);

  const [filteredResourceData, setFilteredResourceData] = useState<
    HospitalResource[]
  >([]);

  useEffect(() => {
    if (isSuccess) {
      setFilteredResourceData(
        reorderByRanking(data.result.data.hospitalResourceResponse)
      );
    }
  }, [data, isSuccess]);

  if (!isSuccess) return <></>;
  const { operations = [], equipments = [], beds = [] } = data.result.data;

  const operationCount = operations.length;
  const equipmentCount = equipments.length;
  const bedColumnCount = beds.length;

  const gridTemplateRowsValue = [
    "40px", // 첫 번째 고정
    ...Array(2 + operationCount + equipmentCount + 2).fill("38px"), // 반복
    ...Array(bedColumnCount).fill("142px"), // 마지막 반복
  ].join(" ");

  return (
    <div
      className={tw(
        "relative left-0 grid grid-cols-[104px_90px] grid-rows-[40px_repeat(5,38px)_142px_142px] grid-flow-col w-max",
        "*:flex *:items-center *:justify-center"
      )}
      style={{ gridTemplateRows: gridTemplateRowsValue }}
    >
      {/* 헤더 */}
      <div className="col-start-1 col-span-2 sticky left-0">
        <div
          className={tw(
            "w-full h-full",
            headerElementStyle,
            "bg-white",
            "rounded-tl-[6px]"
          )}
        />
        {/* 좌/우 스크롤 시 좌측상단 튀어나옴 덮기 용도 */}
        <div className="absolute top-0 left-0 w-[100px] h-full bg-white z-1" />
      </div>

      <div className={tw(headerElementStyle, "col-start-1 col-span-2")}>
        <div className={tw(headerMainTextStyle)}>순위 </div>
      </div>
      <div
        className={tw(
          headerElementStyle,

          "col-start-1 col-span-2"
        )}
      >
        <div className={tw(headerMainTextStyle)}>소요시간 </div>
        <div className={tw(headerSubTextStyle, "text-gray-g100")}>(거리)</div>
      </div>
      {/* 시/수술 헤더 */}
      {operations.length !== 0 && (
        <>
          <div
            className={tw(headerElementStyle)}
            style={{
              gridRow: `span ${operations.length}`,
            }}
          >
            <div className={tw(headerMainTextStyle)}>시/수술</div>
          </div>
          {operations.map((operation) => (
            <div
              key={operation}
              className={tw(headerElementStyle, "col-end-3", "left-[104px]")}
            >
              <div className={tw(headerMainTextStyle)}>
                {
                  operationCodeToName[operation].split("] ")[
                    operationCodeToName[operation].split("] ").length - 1
                  ]
                }
              </div>
            </div>
          ))}
        </>
      )}
      {/* 장비정보 헤더 */}
      {equipments.length !== 0 && (
        <>
          <div
            className={tw(headerElementStyle)}
            style={{
              gridRow: `span ${equipments.length}`,
            }}
          >
            <div className={tw(headerMainTextStyle)}>장비정보</div>
          </div>
          {equipments.map((equipment) => (
            <div
              key={equipment}
              className={tw(headerElementStyle, "col-end-3", "left-[104px]")}
            >
              <div className={tw(headerMainTextStyle)}>
                {equipmentCodeToName[equipment]}
              </div>
            </div>
          ))}
        </>
      )}
      <div
        className={tw(
          headerElementStyle,
          "row-span-3",
          beds.length === 1 && "rounded-bl-[6px] border-b-[1px]"
        )}
      >
        <div className={tw(headerMainTextStyle)}>응급실</div>
      </div>
      <div className={tw(headerElementStyle, "col-end-3", "left-[104px]")}>
        <div className={tw(headerMainTextStyle)}>이송예정</div>
      </div>
      <div className={tw(headerElementStyle, "col-end-3", "left-[104px]")}>
        <div className={tw(headerMainTextStyle)}>중증질환군</div>
      </div>
      <div
        className={tw(
          "w-full h-full flex flex-col items-center justify-center",
          headerElementStyle,
          "col-end-3",
          "left-[104px]",
          beds.length === 1 && "border-b-[1px]"
        )}
      >
        <div className={tw(headerMainTextStyle, "leading-[22px]")}>
          {"병상 잔여율"}
        </div>
        <div
          className={tw(headerSubTextStyle, "text-gray-g100 leading-[16px]")}
        >
          (1시간 전 대비)
        </div>
        <div className="flex flex-col gap-[4px] mt-[15px] w-fit">
          <div className="flex items-center gap-[6px]">
            <div className="w-[10px] h-[10px] rounded-[2px] bg-gray-g50" />
            <div className="text-xs font-medium text-gray-g200">1시간 전</div>
          </div>
          <div className="flex items-center gap-[6px]">
            <div className="w-[10px] h-[10px] rounded-[2px] bg-purple-100" />
            <div className="text-xs font-medium text-gray-g200">현재</div>
          </div>
        </div>
      </div>
      {beds.map(
        (bed, index) =>
          index !== 0 && (
            <>
              <div className="sticky left-0 z-10">
                <div
                  className={tw(
                    "w-full h-full flex flex-col items-center justify-center",
                    headerElementStyle,
                    "rounded-bl-[6px] border-b-[1px]"
                  )}
                >
                  <div className={tw(headerMainTextStyle, "leading-[22px]")}>
                    {
                      bedCodeToName[bed].split("] ")[
                        bedCodeToName[bed].split("] ").length - 1
                      ]
                    }
                  </div>
                </div>
                {/* 스크롤 시 좌측하단 튀어나옴 덮기 용도 */}
                <div className="absolute top-0 left-0 w-[100px] h-full bg-white z-1" />
              </div>
              <div
                className={tw(
                  "w-full h-full flex flex-col items-center justify-center",
                  headerElementStyle,
                  "col-end-3",
                  "border-b-[1px]",
                  "left-[104px]"
                )}
              >
                <div className={tw(headerMainTextStyle, "leading-[22px]")}>
                  {"병상 잔여율"}
                </div>
                <div
                  className={tw(
                    headerSubTextStyle,
                    "text-gray-g100 leading-[16px]"
                  )}
                >
                  (1시간 전 대비)
                </div>
                <div className="flex flex-col gap-[4px] mt-[15px] w-fit">
                  <div className="flex items-center gap-[6px]">
                    <div className="w-[10px] h-[10px] rounded-[2px] bg-gray-g50"></div>
                    <div className="text-xs font-medium text-gray-g200">
                      1시간 전
                    </div>
                  </div>
                  <div className="flex items-center gap-[6px]">
                    <div className="w-[10px] h-[10px] rounded-[2px] bg-purple-100"></div>
                    <div className="text-xs font-medium text-gray-g200">
                      현재
                    </div>
                  </div>
                </div>
              </div>
            </>
          )
      )}

      {/* 각 행 */}
      {filteredResourceData.map((resource, index) => (
        <>
          <div
            className={tw(
              "w-[130px] bg-gray-g10",
              "border-solid border-t-[1px] border-gray-g30",
              index === 0 && "sticky z-10 left-[194px]",
              index === data.result.data.hospitalResourceResponse.length - 1 &&
                "border-r-[1px] rounded-tr-[6px]"
            )}
          >
            <div
              className={tw(
                "w-full h-full flex items-center justify-center gap-[6px]",
                "text-sm font-semibold text-gray-g400",
                index === 0 && "bg-purple-100/12"
              )}
            >
              {resource.hospitalName === followUpHospital && (
                <FollowUpIcon className="w-[16px] h-[16px]" />
              )}
              {hospitalNameToShort[resource.hospitalName]}
            </div>
          </div>
          <div
            className={tw(
              topLeftBorderStyle,
              index === 0 && "sticky z-10 left-[194px] bg-white",
              index === data.result.data.hospitalResourceResponse.length - 1 &&
                "border-r-[1px]"
            )}
          >
            <div
              className={tw(contentMainTextStyle)}
            >{`${resource.ranking}순위`}</div>
          </div>
          <div
            className={tw(
              topLeftBorderStyle,
              "gap-[4px]",
              index === 0 && "sticky z-10 left-[194px] bg-white",
              index === data.result.data.hospitalResourceResponse.length - 1 &&
                "border-r-[1px]"
            )}
          >
            <div className={tw(contentMainTextStyle)}>
              {resource.restTime ? `${resource.restTime}분 ` : "-"}
            </div>
            <div className={"text-sm font-medium text-gray-g100"}>
              {resource.restDistance ? `(${resource.restDistance}km)` : "-"}
            </div>
          </div>
          {operations.map((operationCode) => {
            const isAvailable = resource.operations.find(
              (operation) => operation.operCode === operationCode
            )?.isAvailable;

            return (
              <div
                className={tw(
                  topLeftBorderStyle,
                  contentMainTextStyle,
                  isAvailable ? "text-green" : "text-red",
                  index === 0 && "sticky z-10 left-[194px] bg-white",
                  index ===
                    data.result.data.hospitalResourceResponse.length - 1 &&
                    "border-r-[1px]"
                )}
              >
                {isAvailable ? "가능" : "불가능"}
              </div>
            );
          })}
          {equipments.map((equipmentCode) => {
            const equipmentAvailable = resource.equipments.find(
              (equipment) => equipment.equipCode === equipmentCode
            )?.rltmEquip;

            return (
              <div
                className={tw(
                  topLeftBorderStyle,
                  contentMainTextStyle,
                  equipmentAvailable ? "text-green" : "text-red",
                  index === 0 && "sticky z-10 left-[194px] bg-white",
                  index ===
                    data.result.data.hospitalResourceResponse.length - 1 &&
                    "border-r-[1px]"
                )}
              >
                {equipmentAvailable ? "가능" : "불가능"}
              </div>
            );
          })}
          <div
            className={tw(
              topLeftBorderStyle,
              contentMainTextStyle,
              index === 0 && "sticky z-10 left-[194px] bg-white",
              index === data.result.data.hospitalResourceResponse.length - 1 &&
                "border-r-[1px]"
            )}
          >
            {resource.o001Transfer ?? "-"}
          </div>
          <div
            className={tw(
              topLeftBorderStyle,
              contentMainTextStyle,
              index === 0 && "sticky z-10 left-[194px] bg-white",
              index === data.result.data.hospitalResourceResponse.length - 1 &&
                "border-r-[1px]"
            )}
          >
            {resource.o001Svdss ?? "-"}
          </div>
          {beds.map((bedCode) => {
            const bedStatus = resource.beds.find(
              (bed) => bed.bedCode === bedCode
            ) ?? {
              rltmCurrentBed: 0,
              stdCurrentBed: 0,
              rltmPastBed: 0,
              stdPastBed: 0,
            };

            // todo: null 값에 대한 정보미제공 UI 표시 로직 수정 필요 (Exist여부 판단 시 null의 경우 추가 -> Ratio계산 후 UI 출력)
            const pastRatio = Math.max(
              Number(
                ((bedStatus.rltmPastBed / bedStatus.stdPastBed) * 100).toFixed(
                  1
                )
              ),
              0
            );
            const isPastRatioExist = !isNaN(pastRatio);
            const currentRatio = Math.max(
              Number(
                (
                  (bedStatus.rltmCurrentBed / bedStatus.stdCurrentBed) *
                  100
                ).toFixed(1)
              ),
              0
            );
            const isCurrentRatioExist = !isNaN(currentRatio);

            return (
              <div
                className={tw(
                  topLeftBorderStyle,
                  "relative flex-col border-b-[1px]",
                  index === 0 && "sticky z-10 left-[194px] bg-white",
                  index ===
                    data.result.data.hospitalResourceResponse.length - 1 &&
                    "border-r-[1px] rounded-br-[6px]"
                )}
              >
                <div className="flex items-center justify-center gap-[20px] w-full h-[112px] pt-[8px] pb-[2px]">
                  <div className="flex flex-col items-center justify-end w-[36px] h-[82px]">
                    <div
                      className="w-[16px] bg-gray-g50 rounded-[2px]"
                      style={{ height: `${pastRatio}%` }}
                    />
                    <div className="text-sm font-regular text-gray-g500">
                      {isPastRatioExist && `${pastRatio}%`}
                    </div>
                  </div>
                  <div className="flex flex-col items-center justify-end w-[36px] h-[82px]">
                    <div
                      className="w-[16px] bg-purple-100 rounded-[2px]"
                      style={{ height: `${currentRatio}%` }}
                    />
                    <div className="text-sm font-regular text-gray-g500">
                      {isCurrentRatioExist && `${currentRatio}%`}
                    </div>
                  </div>
                </div>
                <div
                  className={tw(
                    "flex items-center justify-center gap-[15px]",
                    "w-full h-[30px] px-[10px] bg-gray-g10"
                  )}
                >
                  {isCurrentRatioExist ? (
                    <>
                      <div className="flex items-center h-[22px]">
                        <div className="text-base font-medium text-purple-100">
                          {bedStatus.rltmCurrentBed}
                        </div>
                        <div className="text-base font-medium text-gray-g500">
                          {`/${bedStatus.stdCurrentBed}`}
                        </div>
                      </div>
                      <BedChangeIcon
                        change={
                          bedStatus.rltmCurrentBed - bedStatus.rltmPastBed
                        }
                      />
                    </>
                  ) : (
                    <div className="text-base font-medium text-gray-g500">
                      정보미제공
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </>
      ))}
    </div>
  );
};

const headerElementStyle = tw(
  "sticky left-0 z-10",
  "bg-gray-g10",
  "border-solid border-t-[1px] border-l-[1px] border-gray-g30"
);

const topLeftBorderStyle = tw(
  "border-solid border-t-[1px] border-l-[1px] border-gray-g30"
);

const headerMainTextStyle = tw(
  "text-sm font-medium text-gray-g300 whitespace-pre-wrap text-center"
);
const headerSubTextStyle = tw("text-xs font-regular text-gray-g200");

const contentMainTextStyle = tw("text-base font-medium text-gray-g500");

const reorderByRanking = (
  resources: HospitalResource[]
): HospitalResource[] => {
  const sorted = [...resources].sort((a, b) => a.ranking - b.ranking);

  const priorityCode = "A1300002";
  const priorityItem = sorted.find(
    (item) => item.hospitalCode === priorityCode
  );
  const others = sorted.filter((item) => item.hospitalCode !== priorityCode);

  return priorityItem ? [priorityItem, ...others] : sorted;
};
